import React, { Children, useEffect, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import { useFormContext } from 'react-hook-form';
import Creatable from 'react-select/creatable';
import styled from 'styled-components';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import { valueEmpty } from '../../helpers';
import { useBreakpoint } from '../BreakpointProvider';
import { IS_CLIENT } from '../../utils';

const ValueContainer = ({ children, ...props }) => {
  const { selectProps } = props;
  const selectInput = React.Children.toArray(children).find(
    // @ts-expect-error: Changed file from js to tsx
    (input) => input.type.name === 'Input' || input.type.name === 'DummyInput'
  );

  return (
    // @ts-expect-error: Changed file from js to tsx
    <components.ValueContainer {...props}>
      <div style={{ width: '100%' }}>
        {/* @ts-expect-error: Changed file from js to tsx */}
        <components.Placeholder {...props}>{selectProps.placeholder}</components.Placeholder>
      </div>

      {selectInput}
      {Children.map(children, (child) => (child && child.type !== components.Placeholder ? child : null))}
    </components.ValueContainer>
  );
};

const DropdownIndicator = (props) => {
  const { userType } = props;
  let indicatorColor = 'u-textOwners';
  switch (userType) {
    case 'owner':
      indicatorColor = '#0e7873';
      break;
    case 'captain':
      indicatorColor = '#00acb7';
      break;
    case 'renter':
      indicatorColor = '#0751c2';
      break;
    default:
      indicatorColor = '#0e7873';
  }

  return (
    <components.DropdownIndicator {...props}>
      <div className="Select-figure">
        <svg
          className="selectIcon"
          xmlns="http://www.w3.org/2000/svg"
          fill={indicatorColor}
          height={16}
          width={19}
          viewBox="0 0 1008 1008"
        >
          <path d="M875.4 349.4c0 6.6-4 13.2-7.9 17.2L521.2 712.8c-4 4-11.9 7.9-17.2 7.9-6.6 0-13.2-4-17.2-7.9L140.5 366.6c-4-4-7.9-10.6-7.9-17.2 0-5.3 4-13.2 7.9-17.2l37-37c4-4 10.6-7.9 17.2-7.9 5.3 0 13.2 4 17.2 7.9L504 587.3l292.1-292.1c4-4 11.9-7.9 17.2-7.9 6.6 0 13.2 4 17.2 7.9l37 37c3.9 4 7.9 11.9 7.9 17.2z" />
        </svg>
      </div>
    </components.DropdownIndicator>
  );
};

const getDropdownIndicator = (userType, dark) => (p) => <DropdownIndicator userType={userType} dark={dark} {...p} />;

const SelectInput = (props) => {
  const {
    options,
    allowCreate,
    name,
    defaultValue,
    className,
    onChange,
    isDisabled,
    defaultLabel,
    resetValue,
    showError,
    dark,
    placeholder,
    callback,
    maxMenuHeight,
    menuShouldBlockScroll = true,
    errorMessage,
    searchable,
    capitalOption,
    onFocus,
    onBlur,
    captureMenuScroll = false,
    userType,
    forcedValue,
    labelName,
    required,
  } = props;

  const { setValue, register, errors, clearErrors, setError, unregister, trigger } = useFormContext();

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  const selectedLabel = defaultLabel || (!valueEmpty(defaultValue) ? defaultValue.toString() : '');

  const [values, setReactSelect] = useState({
    selectedOption: null,
  });

  useEffect(() => {
    setReactSelect({
      selectedOption: !valueEmpty(defaultValue) ? { label: selectedLabel, value: defaultValue.toString() } : null,
    });
  }, []);

  useEffect(() => {
    if (!forcedValue) return;

    setReactSelect({
      selectedOption: forcedValue,
    });
  }, [forcedValue]);

  const handleOnChange = (selectedOption) => {
    const { value } = selectedOption;

    if (value) {
      clearErrors(name);
    } else {
      // @ts-expect-error: Changed file from js to tsx
      setError(name);
    }

    setValue(name, value);
    setReactSelect({ selectedOption });
    onChange(name, value);
    trigger(name);
  };

  useEffect(() => {
    if (resetValue) {
      setReactSelect({ selectedOption: null });
      setValue(name, '', { shouldDirty: true });
    }
  }, [name, resetValue, setValue]);

  useEffect(() => {
    register(
      name,
      required
        ? {
            validate: (value) => !valueEmpty(value),
            required: errorMessage || 'This field is required',
          }
        : {
            required: false,
          }
    );

    return () => {
      unregister(name);
    };
  }, [name, register, unregister, required, errorMessage]);

  const styles = {
    input: (provided) => ({
      ...provided,
      transform: 'translate(2px, 7px)',
      position: 'absolute',
      margin: 0,
      padding: 0,
    }),
    valueContainer: (provided) => ({
      ...provided,
      overflow: 'initial',
    }),
    placeholder: () => ({
      width: '100%',
    }),
  };

  const inputRef = useRef();

  const focusElement = () => {
    if (onFocus) {
      onFocus();
    }
    addOffset();
  };

  const addOffset = () => {
    if (smallScreen && IS_CLIENT) {
      setTimeout(() => {
        document.body.classList.add('offsetInput');
      }, 200);
    }
  };

  const removeOffset = () => {
    document.body.classList.remove('offsetInput');
  };

  const blurElement = () => {
    if (onBlur) {
      onBlur();
    }
    removeOffset();
  };

  const minHeight = smallScreen ? 190 : 300;

  useEffect(
    () => () => {
      removeOffset();
    },
    []
  );

  const inputId = `${name}_input_id`;
  return (
    <div className="Select-root" ref={inputRef}>
      <div
        className={`Select ${userType} u-sizeFull ${capitalOption ? 'Select-capitalizeFirstLetter' : ''} ${
          dark ? 'Select-dark' : ''
        }`}
        data-testid={`component-selectInput-${name}-input`}
      >
        {allowCreate ? (
          <Creatable
            id={name}
            inputId={inputId}
            isDisabled={isDisabled}
            options={options}
            components={{
              DropdownIndicator: getDropdownIndicator(userType, dark),
              ValueContainer,
            }}
            className={`Select ${searchable ? '' : 'Select_notSearchable'}   ${className || ''} ${
              showError && errors[name] ? ' is-invalid' : ''
            }`}
            classNamePrefix="Select"
            formatCreateLabel={(inputValue) => `Other: ${inputValue}`}
            onChange={handleOnChange}
            value={values.selectedOption || null}
            placeholder={placeholder}
            name={name}
            maxMenuHeight={maxMenuHeight || minHeight}
            captureMenuScroll={captureMenuScroll}
            menuShouldBlockScroll={menuShouldBlockScroll}
            onMenuOpen={() => {
              if (callback) {
                return callback(true);
              }
              return undefined;
            }}
            onMenuClose={() => {
              if (callback) {
                removeOffset();
                return callback(false);
              }
              return undefined;
            }}
            isSearchable={searchable}
            styles={styles}
            onBlur={() => blurElement()}
            onFocus={() => focusElement()}
            menuShouldScrollIntoView
          />
        ) : (
          <Select
            id={name}
            inputId={inputId}
            isDisabled={isDisabled}
            options={options}
            components={{
              DropdownIndicator: getDropdownIndicator(userType, dark),
              ValueContainer,
            }}
            className={`Select ${searchable ? '' : 'Select_notSearchable'}   ${className || ''} ${
              showError && errors[name] ? ' is-invalid' : ''
            }`}
            classNamePrefix="Select"
            noOptionsMessage={({ inputValue }) => `No available: ${inputValue}`}
            onChange={handleOnChange}
            value={values.selectedOption || null}
            placeholder={placeholder}
            name={name}
            maxMenuHeight={maxMenuHeight || minHeight}
            captureMenuScroll={captureMenuScroll}
            menuShouldBlockScroll={menuShouldBlockScroll}
            onMenuOpen={() => {
              if (callback) {
                return callback(true);
              }
              return undefined;
            }}
            onMenuClose={() => {
              if (callback) {
                removeOffset();
                return callback(false);
              }
              return undefined;
            }}
            isSearchable={searchable}
            styles={styles}
            onBlur={() => blurElement()}
            onFocus={() => focusElement()}
            menuShouldScrollIntoView
          />
        )}
        <HiddenLabel htmlFor={inputId}>{labelName}</HiddenLabel>
        {showError && (
          <ErrorMessage
            className="u-posAbsolute"
            error={errors[name]}
            message={`${errorMessage || 'This field is required'} `}
          />
        )}
      </div>
    </div>
  );
};

const HiddenLabel = styled.label`
  display: none;
`;

SelectInput.defaultProps = {
  options: [],
  allowCreate: false,
  name: '',
  onChange: () => null,
  defaultLabel: '',
  showError: true,
  placeholder: 'Select',
  dark: false,
  errorMessage: '',
  searchable: true,
  onFocus: () => null,
  onBlur: () => null,
  userType: 'owner',
  required: true,
};

export default SelectInput;
